import { useState, useEffect } from 'react';

const heroImages = [
  'https://res.cloudinary.com/damiraph/image/upload/v1765886420/Hero1_tmlauc.png',
  'https://res.cloudinary.com/damiraph/image/upload/v1765886420/Hero2_feluzv.png',
  'https://res.cloudinary.com/damiraph/image/upload/v1765886421/Hero3_qeknws.png',
  'https://res.cloudinary.com/damiraph/image/upload/v1765886420/Hero4_n9smlo.png',
];

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6 flex-shrink-0" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 512 512" className="w-6 h-6 flex-shrink-0" fill="currentColor">
      <path d="M48 59.49v393a4.33 4.33 0 007.37 3.07L260 256 55.37 56.42A4.33 4.33 0 0048 59.49zM345.8 174L89.22 32.64l-.16-.09c-4.42-2.4-8.62 3.58-5 7.06l201.06 198.18zM84.08 472.39c-3.64 3.48.56 9.46 5 7.06l.16-.09L345.8 338l-60.64-63.23zM449.38 231l-71.65-39.46L310.36 256l67.37 64.43L449.38 281c19.49-10.77 19.49-39.23 0-50z" />
    </svg>
  );
}

export default function Hero() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        setFade(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen bg-white flex flex-col items-center justify-center overflow-hidden">

      {/* Subtle blue glow behind headline */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(34,40,182,0.06) 0%, transparent 70%)' }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 pt-28 pb-16 w-full max-w-5xl mx-auto">

        {/* Eyebrow badge */}
        <div className="inline-flex items-center gap-2 border border-gray-200 bg-gray-50 rounded-full px-4 py-1.5 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4f56d9] animate-pulse" />
          <span
            className="text-xs font-medium text-gray-500 tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            QR · Virtual · In-person — all in one place
          </span>
        </div>

        {/* Main headline */}
        <h1
          className="text-5xl sm:text-6xl lg:text-[80px] font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-6 max-w-4xl"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Attendance tracking,{' '}
          <span style={{ color: '#2228B6' }}>done right.</span>
        </h1>

        {/* Subtext */}
        <p className="text-lg sm:text-xl text-gray-500 max-w-2xl leading-relaxed mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Stop wasting hours on roll calls. Let your members check in with a QR scan and get
          real-time reports without lifting a finger.
        </p>

        {/* App store buttons */}
        <div id="download" className="flex flex-col sm:flex-row items-center gap-3 mb-16">
          <a
            href="https://apps.apple.com/gb/app/inked/id6761901067"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gray-900 text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-gray-800 transition-colors min-w-[180px]"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <AppleIcon />
            <div className="text-left">
              <div className="text-[10px] text-gray-400 leading-none">Download on the</div>
              <div className="text-sm font-bold leading-tight">App Store</div>
            </div>
          </a>

          <a
            href="https://play.google.com/store/apps/details?id=fit.inked.inked"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gray-900 text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-gray-800 transition-colors min-w-[180px]"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <PlayIcon />
            <div className="text-left">
              <div className="text-[10px] text-gray-400 leading-none">Get it on</div>
              <div className="text-sm font-bold leading-tight">Google Play</div>
            </div>
          </a>
        </div>

        {/* Hero image carousel */}
        <div className="w-full max-w-4xl rounded-2xl overflow-hidden border border-gray-200 shadow-xl shadow-gray-200/60">
          <div
            className="w-full transition-opacity duration-400"
            style={{ opacity: fade ? 1 : 0 }}
          >
            <img
              src={heroImages[currentImageIndex]}
              alt="Inked app"
              className="w-full h-auto block"
            />
          </div>
        </div>

        {/* Carousel dots */}
        <div className="flex gap-2 mt-5">
          {heroImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentImageIndex(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentImageIndex ? 'w-6 bg-[#2228B6]' : 'w-1.5 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Stats bar */}
      <div className="relative z-10 w-full border-t border-gray-100 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0">
          {[
            { value: '50K+', label: 'Members tracked' },
            { value: '10h+', label: 'Saved per week' },
            { value: '99.9%', label: 'Uptime' },
            { value: '< 3s', label: 'Average check-in' },
          ].map(({ value, label }) => (
            <div key={label} className="text-center md:border-r md:last:border-r-0 border-gray-200">
              <div
                className="text-2xl font-bold text-gray-900 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {value}
              </div>
              <div className="text-xs text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
